import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {CenterBox, WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, flexAbsoluteCenter, mb, p, pb, position, positionRelative, zIndex} from "@pg-design/helpers-css";
import {HorizontalListIcon, InfoIcon, SettingsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {appendQueryString, IQuery, parseSearch} from "@pg-mono/request";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {isRailTransportAvailable} from "../../maps/utils/is_rail_transport_available";
import {useCookieBotObserver} from "../../navigation/hooks/use_cookie_bot_observer";
import {Country} from "../../region/types/Country";
import {SlotOrderType} from "../../search/components/search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchFormWithDrawer} from "../../search/components/SearchFormWithDrawer";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {useSearchForm} from "../../search/hooks/use_search_form";
import {ISearchFiltersFormValues} from "../../search/types/ISearchFiltersFormValues";
import {openSidebarHit, sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {parseQueryParamToInt} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {MapOfferPreview} from "../components/map_offer/MapOfferPreview";
import {OfferListMap} from "../components/OfferListMap";
import {ExpandablePanelMapHeader} from "../components/os_map_for_offers/ExpandablePanelMapHeader";
import {ExpandablePanelMapLayers} from "../components/os_map_for_offers/ExpandablePanelMapLayers";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../detail/components/property_list/OfferDetailPropertyListTable";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {useMapOfferListData} from "../hooks/use_map_offer_list_data";
import {useMapOfferListQuery} from "../hooks/use_map_offer_list_query";
import {useOfferListMapTransportRoutes} from "../hooks/use_offer_list_map_transport_routes";
import {useParseFriendlyUrl} from "../hooks/use_parse_friendly_url";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../list/components/OfferListPropertiesModal";
import {getCountryName} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useGetMapRegionsQuery} from "../map/api/get_map_region";
import {MapGeneratedDescription} from "../map/components/MapGeneratedDescription";
import {MapHeader} from "../map/components/MapHeader";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {hitGTMOfferSelectItem} from "../tracking/ecommerce_events/offer/hit_gtm_offer_select_item";
import {OfferType} from "../types/OfferType";

export const OfferListMapView = () => {
    const {isMobile} = useUserDevice();
    const [isHoliday, setIsHoliday] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [offerId, setOfferId] = useState<number | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showGeneratedDescription, setShowGeneratedDescription] = useState(false);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
    const [isPricePanelOpen, setIsPricePanelOpen] = useState(false);
    const theme = useTheme();
    const location = useLocation();

    const selectedRegion = useSelector((state: IRPStore) => state.offerListMap.selectedRegion);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const pageTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {transportRoutes} = useOfferListMapTransportRoutes();
    const hasRailTransport = isRailTransportAvailable(transportRoutes);
    const mapOfferListQuery = useMapOfferListQuery();
    const {offers, count, propertiesCount, isLoading, isError, latestQuery} = useMapOfferListData({
        mapOfferListQuery: mapOfferListQuery
    });
    const {urlParams} = useParseFriendlyUrl();
    const {isCookieBotOpen} = useCookieBotObserver();

    // Map regions data
    const {data: offerListRegion} = useGetMapRegionsQuery({region: selectedRegion.map((region) => region.id)});

    const gtmMeta = {
        listName: pageTitle,
        listId: "mapa"
    };

    const generatedDescriptionAllowed =
        offerListRegion?.results.length === 1 &&
        !!latestQuery.type &&
        parseQueryParamToInt(latestQuery.type) === OfferType.FLAT &&
        isEmpty(omit(latestQuery, ["type", "region"]));

    /**
     * Lifecycle
     */
    useEffect(() => {
        const query = parseSearch(location.search);
        const isHoliday = query.is_holiday === "1";
        setIsHoliday(isHoliday);
    }, []);

    useEffect(() => {
        if (isLoading || isInitialized) {
            return;
        }

        setIsInitialized(true);
    }, [isLoading]);

    // Trigger search hit when offers are loaded
    useEffect(() => {
        if (!isLoading && offers && offers?.length > 0) {
            hitSearch();
        }
    }, [isLoading, offers?.length]);

    // Top Offset of the blend under search fields
    const blendHeightForFiltersWithDrawer = `${navigationHeight + searchFormWithDrawerHeight}px`;

    /**
     * Sidebar implementation
     */
    const dispatch = useDispatch();
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);

    /**
     * Callbacks
     */

    const {openLocationSearch} = useSearchForm();
    const handleMapHeaderClick = () => {
        openLocationSearch();
    };

    // map will flicker without useCallback
    const onMarkerClick = useCallback(
        (offerId: number) => {
            const offer = offers?.find((offer) => offer.id === offerId);

            if (!offer) {
                notifyBugsnag(
                    {
                        name: "NonExistentInvestmentSelected",
                        message: `User clicked on non-existent investment with offerId: ${offerId}`
                    },
                    "onMarkerClick - selected non-existent investment"
                );
                return;
            }

            hitGTMOfferSelectItem({
                offer: {
                    ...offer,
                    configuration: {
                        ...offer.configuration,
                        // The following properties are not in the offer, but there are in the API query
                        limited_presentation: false,
                        display_type: OfferDisplayType.FOR_SALE
                    }
                },
                listId: gtmMeta.listId,
                listName: gtmMeta.listName
            });
            setIsSidebarOpen(true);
            gtmEventMapList(MapGTMActions.PIN, offerId);
            openSidebarHit(ViewType.OFFER_LIST_MAP, offerId);
            setOfferId(offerId);
        },
        [offers, gtmMeta.listId, gtmMeta.listName]
    );

    // Sidebar implementation
    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        setOfferId(null);
    };

    const handleSetOfferId = (offerId: number | null) => {
        setOfferId(offerId);
    };

    const getGoBackToListUrl = () => {
        const countryId = latestQuery.country ? Number(latestQuery.country) : Country.POLAND;
        if (countryId !== Country.POLAND && urlParams.friendlySlug) {
            const url = rpAppLink.offer.listFriendlyAbroad({
                friendlySlug: urlParams.friendlySlug,
                country: getCountryName(countryId),
                abroadRegionSlug: urlParams.abroadRegionSlug
            });
            return appendQueryString(url, omit(latestQuery, ["country", "region", "region_name", "type"]));
        }

        if (urlParams.friendlySlug) {
            return rpAppLink.offer.listFriendly({friendlySlug: urlParams.friendlySlug});
        }

        if (location.pathname === rpAppPath.multiOfferList.map) {
            const url = rpAppLink.offer.multiList();
            return appendQueryString(url, latestQuery as IQuery);
        }
        return rpAppLink.offer.list();
    };

    const hitSearch = () => {
        const searchRegions: ISearchRegion[] = selectedRegion.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData({
            offers_count: count,
            properties_count: propertiesCount || 0,
            latestQuery,
            searchRegions,
            query: parseSearch(location.search),
            travel_time: travelTime,
            locationSearchTab
        });

        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers?.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        // When sidebar is open and filters are changing update properties list
        if (isSidebarOpen && offerId) {
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1},
                {...toQueryValues(propertyListTableFiltersFields, latestSearchFilters)}
            )(dispatch);
        }

        // gtm
        gtmEventOffersCount(count);
        // algolytics
        offerListSearchHit(ViewType.OFFER_LIST_MAP, searchData, offerIds, offerNames, vendorIds, vendorNames, []);
        // FacebookPixel.track("Search");
    };

    const handleListButtonClick = () => gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.LISTING);

    const generatedDescription = offerListRegion?.results.length ? (
        <MapGeneratedDescription
            generatedDescriptionAllowed={generatedDescriptionAllowed}
            onHideGenericDescription={() => setShowGeneratedDescription(false)}
            region={offerListRegion?.results[0]}
            showGeneratedDescription={showGeneratedDescription}
        />
    ) : null;

    /**
     * Render
     */

    if (!isInitialized) {
        return (
            <div css={viewLoaderHolder}>
                <CenterBox>
                    <Loader size="lg" />
                </CenterBox>

                {generatedDescription}
            </div>
        );
    }

    return (
        <div css={[positionRelative]}>
            <div css={offerListMapWrapper}>
                <div css={searchWrapper}>
                    <ExpandablePanelMapHeader
                        offersCount={count}
                        propertiesCount={propertiesCount}
                        isOpen={isInfoPanelOpen}
                        generatedDescriptionAllowed={generatedDescriptionAllowed}
                        handleClose={() => setIsInfoPanelOpen(false)}
                        onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                    />
                    <ExpandablePanelMapLayers transformRoutes={transportRoutes} isOpen={isPricePanelOpen} handleClose={() => setIsPricePanelOpen(false)} />
                    <WideContent>
                        <SearchFormWithDrawer
                            listFriendlyLink={rpAppLink.offer.mapFriendly}
                            abroadListFriendlyLink={({
                                friendlySlug,
                                country,
                                abroadRegionSlug
                            }: {
                                friendlySlug: string;
                                country: string;
                                abroadRegionSlug?: string;
                            }) => {
                                return abroadRegionSlug
                                    ? rpAppLink.offer.mapAbroadRegion({
                                          friendlySlug,
                                          country,
                                          abroadRegionSlug
                                      })
                                    : rpAppLink.offer.mapAbroadCountry({friendlySlug, country});
                            }}
                            multiRegionLink={rpAppLink.offer.multiListMap}
                            latestQuery={latestQuery}
                            hasParentBeenInitialized={isInitialized}
                            sortActive={false}
                            // Blend will start on the bottom line of the Search component
                            // TODO: Sometimes blend is relative to their elements, navigationHeight should be removed from atoms/DesktopBlend implementation
                            blendTop={`${searchFormWithDrawerHeight - navigationHeight}px`}
                            blendHeightDecrease={blendHeightForFiltersWithDrawer}
                            isMap
                            showMobileTopSearchTrigger={count > 0}
                            slotOrder={SlotOrderType.ROW_REVERSE}
                            showTopFilters
                            css={[mb(0), zIndex(2)]}
                            disableLocationSearch={isCookieBotOpen}
                        />
                    </WideContent>
                </div>

                <div css={mapHolder}>
                    {hasRailTransport && (
                        <button
                            css={[sideButton, {top: calculateRemSize(3)}]}
                            onClick={() => {
                                setIsPricePanelOpen((prev) => !prev);
                                setIsInfoPanelOpen(false);
                            }}
                        >
                            <SettingsIcon
                                size="2.4"
                                wrapperColor="transparent"
                                wrapperSize="2.4"
                                fill={isPricePanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                            />
                        </button>
                    )}
                    <button
                        css={[sideButton, {top: calculateRemSize(11)}]}
                        onClick={() => {
                            setIsInfoPanelOpen((prev) => !prev);
                            setIsPricePanelOpen(false);
                        }}
                    >
                        <InfoIcon
                            size="2.4"
                            wrapperColor="transparent"
                            wrapperSize="2.4"
                            fill={isInfoPanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                        />
                    </button>
                    <MapSize fullWidth={!isSidebarOpen}>
                        <OfferListMap
                            transportRoutes={transportRoutes}
                            onMarkerClick={onMarkerClick}
                            offers={offers}
                            isLoading={isLoading}
                            isError={isError}
                            isHoliday={isHoliday}
                        />
                    </MapSize>

                    <>
                        <Button
                            href={getGoBackToListUrl()}
                            onClick={handleListButtonClick}
                            iconLeft={HorizontalListIcon}
                            css={listButton}
                            variant="filled_primary"
                        >
                            Lista
                        </Button>
                        {!isMobile && (
                            <MapHeader
                                pageTitle={pageTitle}
                                offerCount={count}
                                propertiesCount={propertiesCount}
                                selectedRegion={selectedRegion}
                                generatedDescriptionAllowed={generatedDescriptionAllowed}
                                onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                                onHeaderClick={handleMapHeaderClick}
                            />
                        )}
                    </>
                    {offerId && (
                        <MapOfferPreview
                            offerId={offerId}
                            handleSetOfferId={handleSetOfferId}
                            onClose={onOfferSidebarClose}
                            latestQuery={latestQuery}
                            mapOfferListQuery={mapOfferListQuery}
                            isOpen={isSidebarOpen}
                        />
                    )}

                    {generatedDescription}
                </div>
            </div>
            <LeadModal />
        </div>
    );
};

const viewLoaderHolder = css`
    height: calc(100vh - 13rem);
`;

export const mapHolder = css`
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow-x: hidden;
`;

export const MapSize = styled.div<{fullWidth: boolean}>`
    position: relative;
    display: flex;
    width: 100%;
    transition: margin-right 500ms;
    transition-delay: 50ms;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        margin-right: ${(props) => (props.fullWidth ? "0" : "42%")};
    }
`;

export const listButton = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        min-width: 15.6rem;
        padding-left: 4rem;
        padding-right: 4rem;
        display: block;
        position: absolute;
        top: 2.5rem;
        left: 2.5rem;
    }
`;

const offerListMapWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 61px);
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        height: calc(100vh - 60px);
    }
`;

const searchWrapper = (theme: Theme) => css`
    display: block;
    width: 100%;
    height: auto;
    background-color: #fff;
    ${pb(3)};
    z-index: 2;
    ${elevation(4)};

    @media (min-width: ${theme.breakpoints.md}) {
        background-color: initial;
        ${pb(0)};
        ${elevation(0)};
    }
`;

const sideButton = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    ${borderRadius(0.5, 0, 0, 0.5)};
    ${position("absolute")};
    ${p(1)};
    right: 0;
    background-color: #fff;
    z-index: 1;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;
import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {CenterBox, WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, flexAbsoluteCenter, mb, p, pb, position, positionRelative, zIndex} from "@pg-design/helpers-css";
import {HorizontalListIcon, InfoIcon, SettingsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {appendQueryString, IQuery, parseSearch} from "@pg-mono/request";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {isRailTransportAvailable} from "../../maps/utils/is_rail_transport_available";
import {useCookieBotObserver} from "../../navigation/hooks/use_cookie_bot_observer";
import {Country} from "../../region/types/Country";
import {SlotOrderType} from "../../search/components/search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchFormWithDrawer} from "../../search/components/SearchFormWithDrawer";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {useSearchForm} from "../../search/hooks/use_search_form";
import {ISearchFiltersFormValues} from "../../search/types/ISearchFiltersFormValues";
import {openSidebarHit, sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {parseQueryParamToInt} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {MapOfferPreview} from "../components/map_offer/MapOfferPreview";
import {OfferListMap} from "../components/OfferListMap";
import {ExpandablePanelMapHeader} from "../components/os_map_for_offers/ExpandablePanelMapHeader";
import {ExpandablePanelMapLayers} from "../components/os_map_for_offers/ExpandablePanelMapLayers";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../detail/components/property_list/OfferDetailPropertyListTable";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {useMapOfferListData} from "../hooks/use_map_offer_list_data";
import {useMapOfferListQuery} from "../hooks/use_map_offer_list_query";
import {useOfferListMapTransportRoutes} from "../hooks/use_offer_list_map_transport_routes";
import {useParseFriendlyUrl} from "../hooks/use_parse_friendly_url";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../list/components/OfferListPropertiesModal";
import {getCountryName} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useGetMapRegionsQuery} from "../map/api/get_map_region";
import {MapGeneratedDescription} from "../map/components/MapGeneratedDescription";
import {MapHeader} from "../map/components/MapHeader";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {hitGTMOfferSelectItem} from "../tracking/ecommerce_events/offer/hit_gtm_offer_select_item";
import {OfferType} from "../types/OfferType";

export const OfferListMapView = () => {
    const {isMobile} = useUserDevice();
    const [isHoliday, setIsHoliday] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [offerId, setOfferId] = useState<number | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showGeneratedDescription, setShowGeneratedDescription] = useState(false);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
    const [isPricePanelOpen, setIsPricePanelOpen] = useState(false);
    const theme = useTheme();
    const location = useLocation();

    const selectedRegion = useSelector((state: IRPStore) => state.offerListMap.selectedRegion);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const pageTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const locationSearchTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {transportRoutes} = useOfferListMapTransportRoutes();
    const hasRailTransport = isRailTransportAvailable(transportRoutes);
    const mapOfferListQuery = useMapOfferListQuery();
    const {offers, count, propertiesCount, isLoading, isError, latestQuery} = useMapOfferListData({
        mapOfferListQuery: mapOfferListQuery
    });
    const {urlParams} = useParseFriendlyUrl();
    const {isCookieBotOpen} = useCookieBotObserver();

    // Map regions data
    const {data: offerListRegion} = useGetMapRegionsQuery({region: selectedRegion.map((region) => region.id)});

    const gtmMeta = {
        listName: pageTitle,
        listId: "mapa"
    };

    const generatedDescriptionAllowed =
        offerListRegion?.results.length === 1 &&
        !!latestQuery.type &&
        parseQueryParamToInt(latestQuery.type) === OfferType.FLAT &&
        isEmpty(omit(latestQuery, ["type", "region"]));

    /**
     * Lifecycle
     */
    useEffect(() => {
        const query = parseSearch(location.search);
        const isHoliday = query.is_holiday === "1";
        setIsHoliday(isHoliday);
    }, []);

    useEffect(() => {
        if (isLoading || isInitialized) {
            return;
        }

        setIsInitialized(true);
    }, [isLoading]);

    // Trigger search hit when offers are loaded
    useEffect(() => {
        if (!isLoading && offers && offers?.length > 0) {
            hitSearch();
        }
    }, [isLoading, offers?.length]);

    // Top Offset of the blend under search fields
    const blendHeightForFiltersWithDrawer = `${navigationHeight + searchFormWithDrawerHeight}px`;

    /**
     * Sidebar implementation
     */
    const dispatch = useDispatch();
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);

    /**
     * Callbacks
     */

    const {openLocationSearch} = useSearchForm();
    const handleMapHeaderClick = () => {
        openLocationSearch();
    };

    // map will flicker without useCallback
    const onMarkerClick = useCallback(
        (offerId: number) => {
            const offer = offers?.find((offer) => offer.id === offerId);

            if (!offer) {
                notifyBugsnag(
                    {
                        name: "NonExistentInvestmentSelected",
                        message: `User clicked on non-existent investment with offerId: ${offerId}`
                    },
                    "onMarkerClick - selected non-existent investment"
                );
                return;
            }

            hitGTMOfferSelectItem({
                offer: {
                    ...offer,
                    configuration: {
                        ...offer.configuration,
                        // The following properties are not in the offer, but there are in the API query
                        limited_presentation: false,
                        display_type: OfferDisplayType.FOR_SALE
                    }
                },
                listId: gtmMeta.listId,
                listName: gtmMeta.listName
            });
            setIsSidebarOpen(true);
            gtmEventMapList(MapGTMActions.PIN, offerId);
            openSidebarHit(ViewType.OFFER_LIST_MAP, offerId);
            setOfferId(offerId);
        },
        [offers, gtmMeta.listId, gtmMeta.listName]
    );

    // Sidebar implementation
    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        setOfferId(null);
    };

    const handleSetOfferId = (offerId: number | null) => {
        setOfferId(offerId);
    };

    const getGoBackToListUrl = () => {
        const countryId = latestQuery.country ? Number(latestQuery.country) : Country.POLAND;
        if (countryId !== Country.POLAND && urlParams.friendlySlug) {
            const url = rpAppLink.offer.listFriendlyAbroad({
                friendlySlug: urlParams.friendlySlug,
                country: getCountryName(countryId),
                abroadRegionSlug: urlParams.abroadRegionSlug
            });
            return appendQueryString(url, omit(latestQuery, ["country", "region", "region_name", "type"]));
        }

        if (urlParams.friendlySlug) {
            return rpAppLink.offer.listFriendly({friendlySlug: urlParams.friendlySlug});
        }

        if (location.pathname === rpAppPath.multiOfferList.map) {
            const url = rpAppLink.offer.multiList();
            return appendQueryString(url, latestQuery as IQuery);
        }
        return rpAppLink.offer.list();
    };

    const hitSearch = () => {
        const searchRegions: ISearchRegion[] = selectedRegion.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData({
            offers_count: count,
            properties_count: propertiesCount || 0,
            latestQuery,
            searchRegions,
            query: parseSearch(location.search),
            travel_time: travelTime,
            locationSearchTab
        });

        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers?.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        // When sidebar is open and filters are changing update properties list
        if (isSidebarOpen && offerId) {
            fetchOfferDetailProperties(
                offerId,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1},
                {...toQueryValues(propertyListTableFiltersFields, latestSearchFilters)}
            )(dispatch);
        }

        // gtm
        gtmEventOffersCount(count);
        // algolytics
        offerListSearchHit(ViewType.OFFER_LIST_MAP, searchData, offerIds, offerNames, vendorIds, vendorNames, []);
        // FacebookPixel.track("Search");
    };

    const handleListButtonClick = () => gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.LISTING);

    const generatedDescription = offerListRegion?.results.length ? (
        <MapGeneratedDescription
            generatedDescriptionAllowed={generatedDescriptionAllowed}
            onHideGenericDescription={() => setShowGeneratedDescription(false)}
            region={offerListRegion?.results[0]}
            showGeneratedDescription={showGeneratedDescription}
        />
    ) : null;

    /**
     * Render
     */

    if (!isInitialized) {
        return (
            <div css={viewLoaderHolder}>
                <CenterBox>
                    <Loader size="lg" />
                </CenterBox>

                {generatedDescription}
            </div>
        );
    }

    return (
        <div css={[positionRelative]}>
            <div css={offerListMapWrapper}>
                <div css={searchWrapper}>
                    <ExpandablePanelMapHeader
                        offersCount={count}
                        propertiesCount={propertiesCount}
                        isOpen={isInfoPanelOpen}
                        generatedDescriptionAllowed={generatedDescriptionAllowed}
                        handleClose={() => setIsInfoPanelOpen(false)}
                        onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                    />
                    <ExpandablePanelMapLayers transformRoutes={transportRoutes} isOpen={isPricePanelOpen} handleClose={() => setIsPricePanelOpen(false)} />
                    <WideContent>
                        <SearchFormWithDrawer
                            listFriendlyLink={rpAppLink.offer.mapFriendly}
                            abroadListFriendlyLink={({
                                friendlySlug,
                                country,
                                abroadRegionSlug
                            }: {
                                friendlySlug: string;
                                country: string;
                                abroadRegionSlug?: string;
                            }) => {
                                return abroadRegionSlug
                                    ? rpAppLink.offer.mapAbroadRegion({
                                          friendlySlug,
                                          country,
                                          abroadRegionSlug
                                      })
                                    : rpAppLink.offer.mapAbroadCountry({friendlySlug, country});
                            }}
                            multiRegionLink={rpAppLink.offer.multiListMap}
                            latestQuery={latestQuery}
                            hasParentBeenInitialized={isInitialized}
                            sortActive={false}
                            // Blend will start on the bottom line of the Search component
                            // TODO: Sometimes blend is relative to their elements, navigationHeight should be removed from atoms/DesktopBlend implementation
                            blendTop={`${searchFormWithDrawerHeight - navigationHeight}px`}
                            blendHeightDecrease={blendHeightForFiltersWithDrawer}
                            isMap
                            showMobileTopSearchTrigger={count > 0}
                            slotOrder={SlotOrderType.ROW_REVERSE}
                            showTopFilters
                            css={[mb(0), zIndex(2)]}
                            disableLocationSearch={isCookieBotOpen}
                        />
                    </WideContent>
                </div>

                <div css={mapHolder}>
                    {hasRailTransport && (
                        <button
                            css={[sideButton, {top: calculateRemSize(3)}]}
                            onClick={() => {
                                setIsPricePanelOpen((prev) => !prev);
                                setIsInfoPanelOpen(false);
                            }}
                        >
                            <SettingsIcon
                                size="2.4"
                                wrapperColor="transparent"
                                wrapperSize="2.4"
                                fill={isPricePanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                            />
                        </button>
                    )}
                    <button
                        css={[sideButton, {top: calculateRemSize(11)}]}
                        onClick={() => {
                            setIsInfoPanelOpen((prev) => !prev);
                            setIsPricePanelOpen(false);
                        }}
                    >
                        <InfoIcon
                            size="2.4"
                            wrapperColor="transparent"
                            wrapperSize="2.4"
                            fill={isInfoPanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                        />
                    </button>
                    <MapSize fullWidth={!isSidebarOpen}>
                        <OfferListMap
                            transportRoutes={transportRoutes}
                            onMarkerClick={onMarkerClick}
                            offers={offers}
                            isLoading={isLoading}
                            isError={isError}
                            isHoliday={isHoliday}
                        />
                    </MapSize>

                    <>
                        <Button
                            href={getGoBackToListUrl()}
                            onClick={handleListButtonClick}
                            iconLeft={HorizontalListIcon}
                            css={listButton}
                            variant="filled_primary"
                        >
                            Lista
                        </Button>
                        {!isMobile && (
                            <MapHeader
                                pageTitle={pageTitle}
                                offerCount={count}
                                propertiesCount={propertiesCount}
                                selectedRegion={selectedRegion}
                                generatedDescriptionAllowed={generatedDescriptionAllowed}
                                onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                                onHeaderClick={handleMapHeaderClick}
                            />
                        )}
                    </>
                    {offerId && (
                        <MapOfferPreview
                            offerId={offerId}
                            handleSetOfferId={handleSetOfferId}
                            onClose={onOfferSidebarClose}
                            latestQuery={latestQuery}
                            mapOfferListQuery={mapOfferListQuery}
                            isOpen={isSidebarOpen}
                        />
                    )}

                    {generatedDescription}
                </div>
            </div>
            <LeadModal />
        </div>
    );
};

const viewLoaderHolder = css`
    height: calc(100vh - 13rem);
`;

export const mapHolder = css`
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow-x: hidden;
`;

export const MapSize = styled.div<{fullWidth: boolean}>`
    position: relative;
    display: flex;
    width: 100%;
    transition: margin-right 500ms;
    transition-delay: 50ms;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        margin-right: ${(props) => (props.fullWidth ? "0" : "42%")};
    }
`;

export const listButton = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        min-width: 15.6rem;
        padding-left: 4rem;
        padding-right: 4rem;
        display: block;
        position: absolute;
        top: 2.5rem;
        left: 2.5rem;
    }
`;

const offerListMapWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 61px);
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        height: calc(100vh - 60px);
    }
`;

const searchWrapper = (theme: Theme) => css`
    display: block;
    width: 100%;
    height: auto;
    background-color: #fff;
    ${pb(3)};
    z-index: 2;
    ${elevation(4)};

    @media (min-width: ${theme.breakpoints.md}) {
        background-color: initial;
        ${pb(0)};
        ${elevation(0)};
    }
`;

const sideButton = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    ${borderRadius(0.5, 0, 0, 0.5)};
    ${position("absolute")};
    ${p(1)};
    right: 0;
    background-color: #fff;
    z-index: 1;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;
